/**
 * StatGrid + StatCard
 *
 * Token-based metric cards used by ReviewImportStep's summary and complete
 * phases. Each card shows an icon (in a rounded tile), a numeric value, and a
 * label. Two sizes:
 *   - 'default' — compact horizontal card (summary phase).
 *   - 'lg'      — larger vertical card (complete/success phase).
 */
import { ReactNode } from 'react'
import { cn } from 'components/UI/cn'

export interface StatItem {
  icon: ReactNode
  value: number
  label: string
  /** Card background token class (e.g. 'bg-surface'). */
  bg: string
  /** Value text token class (e.g. 'text-fg'). */
  textColor: string
  /** Icon-tile background token class. Defaults to neutral gray. */
  iconBg?: string
  /** Card border token class. Defaults to the neutral line border. */
  border?: string
  /** Label text token class. Defaults to secondary gray. */
  labelColor?: string
}

type StatSize = 'default' | 'lg'

interface StatGridProps {
  items: StatItem[]
  className?: string
  size?: StatSize
}

/** Renders a single row of equal-width metric cards. */
export function StatGrid({
  items,
  className,
  size = 'default',
}: StatGridProps) {
  return (
    <div className={cn('flex gap-3', className)}>
      {items.map((item) => (
        <StatCard key={item.label} size={size} {...item} />
      ))}
    </div>
  )
}

/** A single metric card with icon tile, value, and label. */
export function StatCard({
  icon,
  value,
  label,
  bg,
  textColor,
  iconBg = 'bg-neutral-100',
  border = 'border-line',
  labelColor = 'text-fg-secondary',
  size = 'default',
}: StatItem & { size?: StatSize }) {
  if (size === 'lg') {
    return (
      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col items-center gap-2 rounded-xl border px-4 py-5 shadow-sm',
          border,
          bg
        )}
      >
        <div
          className={cn(
            'flex size-12 shrink-0 items-center justify-center rounded-lg',
            iconBg
          )}
        >
          {icon}
        </div>
        <p
          className={cn(
            'text-3xl font-bold leading-none tabular-nums',
            textColor
          )}
        >
          {value.toLocaleString()}
        </p>
        <p className={cn('max-w-full text-center text-sm', labelColor)}>
          {label}
        </p>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex min-w-0 flex-1 items-center justify-center gap-3 rounded-xl border px-3 py-4 shadow-sm',
        border,
        bg
      )}
    >
      <div
        className={cn(
          'flex size-9 shrink-0 items-center justify-center rounded-lg',
          iconBg
        )}
      >
        {icon}
      </div>
      <div className="flex min-w-0 flex-col">
        <p
          className={cn(
            'text-lg font-bold leading-none tabular-nums',
            textColor
          )}
        >
          {value.toLocaleString()}
        </p>
        <p className={cn('truncate text-xs', labelColor)}>{label}</p>
      </div>
    </div>
  )
}
